import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Phone, Award, GraduationCap, Gavel } from "lucide-react";
import { ATTORNEYS } from "../mock";

const AttorneyDetail = () => {
  const { slug } = useParams();
  const attorney = ATTORNEYS.find((a) => a.slug === slug);

  if (!attorney) {
    return <Navigate to="/attorneys" replace />;
  }

  const other = ATTORNEYS.find((a) => a.slug !== slug);

  return (
    <div className="bg-white">
      {/* Header block */}
      <section className="bg-nw-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, #b18f5f 0%, transparent 50%)",
        }} />
        <div className="container-nw relative py-12 md:py-16">
          <Link
            to="/attorneys"
            className="inline-flex items-center gap-2 text-white/70 hover:text-nw-gold-light text-xs uppercase tracking-[0.2em] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Attorneys
          </Link>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden bg-nw-navy-light shadow-2xl">
                <img
                  src={attorney.photo}
                  alt={`${attorney.firstName} ${attorney.lastName}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="text-nw-gold-light text-xs uppercase tracking-[0.4em] mb-4">
                {attorney.title}
              </div>
              <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-tight">
                {attorney.firstName} {attorney.lastName}
              </h1>
              <div className="h-0.5 w-20 bg-nw-gold mt-6 mb-6" />
              <div className="flex flex-wrap gap-2 mb-8">
                {attorney.practiceAreas.map((pa) => (
                  <span
                    key={pa}
                    className="text-xs text-white/90 border border-white/25 bg-white/5 px-3 py-1.5"
                  >
                    {pa}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <a
                  href={`mailto:${attorney.email}`}
                  className="flex items-center gap-2 text-white/85 hover:text-nw-gold-light"
                >
                  <Mail className="w-4 h-4 text-nw-gold" />
                  {attorney.email}
                </a>
                <a
                  href={`tel:${attorney.phone}`}
                  className="flex items-center gap-2 text-white/85 hover:text-nw-gold-light"
                >
                  <Phone className="w-4 h-4 text-nw-gold" />
                  {attorney.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="container-nw grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-3">
              Biography
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy mb-2">
              About {attorney.firstName}
            </h2>
            <div className="h-0.5 w-14 bg-nw-gold mb-8" />
            <div className="prose prose-lg max-w-none space-y-5 text-nw-navy/80 leading-relaxed">
              {attorney.bio.map((p, i) => (
                <p key={i} className="text-base md:text-[17px] leading-[1.75]">
                  {p}
                </p>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-8">
            {attorney.credentials.admitted && attorney.credentials.admitted.length > 0 && (
              <div className="bg-nw-cream p-7 border-t-2 border-nw-gold">
                <div className="flex items-center gap-3 mb-4">
                  <Gavel className="w-5 h-5 text-nw-gold" strokeWidth={1.5} />
                  <h3 className="font-serif-display text-xl text-nw-navy">
                    Admitted
                  </h3>
                </div>
                <ul className="space-y-2.5 text-sm text-nw-navy/80">
                  {attorney.credentials.admitted.map((c) => (
                    <li key={c} className="flex gap-2">
                      <span className="text-nw-gold">&mdash;</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-nw-cream p-7 border-t-2 border-nw-gold">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="w-5 h-5 text-nw-gold" strokeWidth={1.5} />
                <h3 className="font-serif-display text-xl text-nw-navy">
                  Education
                </h3>
              </div>
              <ul className="space-y-2.5 text-sm text-nw-navy/80">
                {attorney.credentials.education.map((c) => (
                  <li key={c} className="flex gap-2">
                    <span className="text-nw-gold">—</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-nw-cream p-7 border-t-2 border-nw-gold">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-5 h-5 text-nw-gold" strokeWidth={1.5} />
                <h3 className="font-serif-display text-xl text-nw-navy">
                  Recognition
                </h3>
              </div>
              <ul className="space-y-2.5 text-sm text-nw-navy/80">
                {attorney.credentials.recognition.map((c) => (
                  <li key={c} className="flex gap-2">
                    <span className="text-nw-gold">—</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {other && (
        <section className="bg-nw-cream py-16">
          <div className="container-nw">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-3">
              Also at Newberry Wieters
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy mb-8">
              Meet {other.firstName} {other.lastName}
            </h2>
            <Link
              to={`/attorneys/${other.slug}`}
              className="group flex flex-col md:flex-row items-stretch bg-white border border-nw-stone card-lift overflow-hidden"
            >
              <div className="md:w-1/3 aspect-[4/5] md:aspect-auto overflow-hidden bg-nw-stone">
                <img
                  src={other.photo}
                  alt={`${other.firstName} ${other.lastName}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="md:w-2/3 p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-2">
                  {other.title}
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-nw-navy group-hover:text-nw-gold transition-colors">
                  {other.firstName} {other.lastName}
                </h3>
                <p className="mt-4 text-nw-navy/70 leading-relaxed">
                  {other.bio[0]}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-nw-gold">
                  View Profile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default AttorneyDetail;
